import * as React from 'react';
import { Modal, Tabs, Table, Alert, Checkbox, Button, Select, AutoComplete, Row, Col, Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { CloseOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { observer, Observer } from 'mobx-react-lite';
import { UserModel } from '../../types/UserModel';
import { ProjectAssignmentVisualStore } from '../../stores';
import UsersManagerStore from '../../stores/UsersManagerStore';
import './EditProjectUsers.less';

const { TabPane } = Tabs;

interface EditProjectUsersProps {
    store: ProjectAssignmentVisualStore;
    usersManagerStore?: UsersManagerStore;
    visible: boolean;
    onClose: () => void;
    projectId: string;
    projectName: string;
}

const EditProjectUsers: React.FC<EditProjectUsersProps> = ({
    store,
    usersManagerStore,
    visible,
    projectId,
    projectName,
    onClose
}) => {
    React.useEffect(() => {
        if (visible && store) {
            store.clearPendingChanges();
            store.initializeSelectedProjectUserIds(projectId);
        }
    }, [visible, store, projectId]);

    if (!visible || !store) {
        return null;
    }

    const filteredAssignedUsers = store.filteredCurrentProjectAssignedUsers;

    const filteredOtherUsers = store.filteredCurrentProjectOtherUsers;

    const assignedUsersOptions = store.currentProjectAssignedUsersOptions;

    const otherUsersOptions = store.currentProjectOtherUsersOptions;

    const handleToggleUserAssignment = (userId: string, isAssigned: boolean) => {
        store.handleToggleUserAssignment(userId, isAssigned);
    };

    const hasAssignedUsers = store.selectedProjectUserIds.size > 0;

    const handleSave = async () => {
        if (!store.hasPendingChanges) {
            onClose();
            return;
        }
        try {
            await store.saveProjectUserAssignments(projectId);
            onClose();
        } catch (error) {
            console.error('Failed to save user assignments and roles:', error);
        }
    };

    const handleCancel = () => {
        store.clearPendingChanges();
        onClose();
    };

    const isGroupAssignment = (userId: string) => {
        return store.isGroupAssignment(userId, projectId);
    };

    const baseColumns: ColumnsType<UserModel> = [
        {
            title: 'Username',
            dataIndex: 'userName',
            key: 'userName',
            sorter: (a, b) => a.userName.localeCompare(b.userName)
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            sorter: (a, b) => (a.email || '').localeCompare(b.email || '')
        },
        {
            title: (
                <span>
                    Project Roles{' '}
                    <Tooltip title="This will be the user’s role on this project after assignment">
                        <InfoCircleOutlined />
                    </Tooltip>
                </span>
            ),
            key: 'userRoles',
            width: 200,
            render: (record: UserModel) => {
                const isGroupOnly = isGroupAssignment(record.id);
                return (
                    <Observer>
                        {() => {
                            const { projectRoles, globalRoles } = store.getAllUserRoles(record.id, projectId);
                            const allRoles = [...projectRoles, ...globalRoles];

                            return (
                                <Select
                                    mode="tags"
                                    placeholder="Select roles"
                                    value={allRoles}
                                    onChange={roles => {
                                        const editableRoles = roles.filter(role => !globalRoles.includes(role));
                                        store.handleUserRolesChange(record.id, editableRoles);
                                    }}
                                    options={usersManagerStore?.rolesWithLabels}
                                    style={{ width: '100%' }}
                                    size="small"
                                    disabled={isGroupOnly}
                                    tagRender={props => {
                                        const { label, value, closable, onClose: onTagClose } = props;
                                        const isGlobalRole = globalRoles.includes(value as string);

                                        return (
                                            <span
                                                className={`role-tag ${isGlobalRole ? 'global-role' : 'project-role'}`}
                                                title={isGlobalRole ? 'Default role (read-only)' : 'Project role'}
                                            >
                                                {label} {isGlobalRole && '(Default role)'}
                                                {!isGlobalRole && closable && (
                                                    <span
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            onTagClose();
                                                        }}
                                                        className="close-icon"
                                                    >
                                                        ×
                                                    </span>
                                                )}
                                            </span>
                                        );
                                    }}
                                />
                            );
                        }}
                    </Observer>
                );
            }
        },
        {
            title: 'Assignment Type',
            key: 'assignmentType',
            render: (record: UserModel) => {
                const type = isGroupAssignment(record.id) ? 'Group' : 'Direct';
                return <span>{type}</span>;
            }
        }
    ];

    const assignedUsersColumns: ColumnsType<UserModel> = [
        ...baseColumns,
        {
            title: '',
            key: 'actions',
            width: 80,
            align: 'center' as const,
            render: (record: UserModel) => {
                return (
                    <Button
                        type="text"
                        icon={<CloseOutlined />}
                        onClick={() => store.handleUnassignUser(record.id)}
                        title={
                            isGroupAssignment(record.id)
                                ? 'Cannot unassign user assigned through group'
                                : 'Unassign User'
                        }
                        danger
                        disabled={isGroupAssignment(record.id)}
                    />
                );
            }
        }
    ];

    const otherUsersColumns: ColumnsType<UserModel> = [
        {
            title: 'Assign',
            key: 'assign',
            width: 60,
            align: 'center' as const,
            render: (record: UserModel) => {
                return (
                    <Checkbox
                        checked={store.selectedProjectUserIds.has(record.id)}
                        onChange={e => {
                            handleToggleUserAssignment(record.id, e.target.checked);
                        }}
                        disabled={isGroupAssignment(record.id)}
                    />
                );
            }
        },
        ...baseColumns
    ];

    return (
        <Modal
            title={`Edit Users for ${projectName}${store.hasPendingChanges ? ' *' : ''}`}
            visible={visible}
            onCancel={handleCancel}
            footer={[
                <Button key="cancel" onClick={handleCancel}>
                    Cancel
                </Button>,
                <Button key="save" type="primary" onClick={handleSave}>
                    Save
                </Button>
            ]}
            width={1000}
            bodyStyle={{ padding: '5px 24px 0px 24px' }}
            className="edit-project-users-modal"
            destroyOnClose
        >
            <Tabs defaultActiveKey="assigned">
                <TabPane tab="Assigned Users" key="assigned">
                    <div>
                        <Row gutter={16} style={{ marginBottom: 16 }}>
                            <Col span={12}>
                                <AutoComplete
                                    style={{ width: '100%' }}
                                    options={assignedUsersOptions
                                        .filter(option =>
                                            option
                                                .toLowerCase()
                                                .includes((store.assignedUsersFilter || '').toLowerCase())
                                        )
                                        .map(option => ({
                                            value: option,
                                            label: <span>{option}</span>
                                        }))}
                                    placeholder="Search assigned users"
                                    value={store.assignedUsersFilter}
                                    onSearch={store.setAssignedUsersFilter}
                                    onSelect={store.setAssignedUsersFilter}
                                    onClear={() => store.setAssignedUsersFilter('')}
                                    allowClear
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') {
                                            store.setAssignedUsersFilter(store.assignedUsersFilter);
                                        }
                                    }}
                                />
                            </Col>
                        </Row>
                        {!hasAssignedUsers ? (
                            <Alert
                                message="No Users Assigned"
                                description={`No users are currently assigned to project "${projectName}".`}
                                type="info"
                                showIcon
                                style={{ marginBottom: 16 }}
                            />
                        ) : (
                            <>
                                <p style={{ marginBottom: 16 }}>
                                    <strong>{filteredAssignedUsers.length}</strong> user
                                    {filteredAssignedUsers.length !== 1 ? 's' : ''}{' '}
                                    {store.assignedUsersFilter
                                        ? `matching "${store.assignedUsersFilter}" of ${store.selectedProjectUserIds.size} total`
                                        : 'assigned to project'}
                                    : <strong>{projectName}</strong>
                                </p>
                                <Table
                                    columns={assignedUsersColumns}
                                    dataSource={filteredAssignedUsers}
                                    rowKey="id"
                                    pagination={{
                                        pageSize: 10,
                                        showSizeChanger: false,
                                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} users`
                                    }}
                                    size="middle"
                                />
                            </>
                        )}
                    </div>
                </TabPane>
                <TabPane tab="Other Users" key="other">
                    <div>
                        <Row gutter={16} style={{ marginBottom: 16 }}>
                            <Col span={12}>
                                <AutoComplete
                                    style={{ width: '100%' }}
                                    options={otherUsersOptions
                                        .filter(option =>
                                            option.toLowerCase().includes((store.otherUsersFilter || '').toLowerCase())
                                        )
                                        .map(option => ({
                                            value: option,
                                            label: <span>{option}</span>
                                        }))}
                                    placeholder="Search all users"
                                    value={store.otherUsersFilter}
                                    onSearch={store.setOtherUsersFilter}
                                    onSelect={store.setOtherUsersFilter}
                                    onClear={() => store.setOtherUsersFilter('')}
                                    allowClear
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') {
                                            store.setOtherUsersFilter(store.otherUsersFilter);
                                        }
                                    }}
                                />
                            </Col>
                        </Row>
                        {store.users.length === 0 ? (
                            <Alert
                                message="No Users Found"
                                description="No users are available in the system."
                                type="info"
                                showIcon
                                style={{ marginBottom: 16 }}
                            />
                        ) : (
                            <>
                                <p style={{ marginBottom: 16 }}>
                                    <strong>{filteredOtherUsers.length}</strong> user
                                    {filteredOtherUsers.length !== 1 ? 's' : ''}{' '}
                                    {store.otherUsersFilter
                                        ? `matching "${store.otherUsersFilter}" of ${store.users.length} total`
                                        : 'in the system'}
                                    . Check the boxes to assign/unassign users to project:{' '}
                                    <strong>{projectName}</strong>
                                </p>
                                <Table
                                    columns={otherUsersColumns}
                                    dataSource={filteredOtherUsers}
                                    rowKey="id"
                                    pagination={{
                                        pageSize: 10,
                                        showSizeChanger: false,
                                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} users`
                                    }}
                                    size="middle"
                                />
                            </>
                        )}
                    </div>
                </TabPane>
            </Tabs>
        </Modal>
    );
};

export default observer(EditProjectUsers);
